import { humanize, titleize } from 'underscore.string';
import { allColumns as shipmentColumns, getColumnDefs, getReferenceNumberGroup, } from '../columns';
import { orderColumns } from '../orders/columns';
const SHIPPER_BLACKLIST = []; // populate later
const CARRIER_BLACKLIST = []; // populate later
const CONTAINER_STATUS = {
    EMPTY_RETURNED: 'Empty Returned',
    ON_WATER: 'On Water',
    AVAILABLE_IN_FUTURE: 'Available In Future',
    PENDING_DISCHARGE: 'Pending Discharge',
    TERMINAL_NOT_AVAILABLE: 'Terminal - Not Available',
    AVAILABLE_NOT_SCHEDULED: 'Available - Not Scheduled',
    AVAILABLE_SCHEDULED: 'Available - Scheduled',
    AT_CUSTOMER_FULL: 'At Customer Full',
    AT_CUSTOMER_EMPTY: 'At Customer Empty',
    UNDER_DISPATCH: 'Under Dispatch',
};
const STANDARD_REFERENCE_NUMBERS = [
    'delivery_order',
    'gate_dropoff',
    'gate_pickup',
    'house_bill_number',
    'isa',
    'master_bol',
    'mg_load_id',
    'mo_number',
    'pu_number',
    'seal_number',
    'tracking_id',
    'truck_order',
    'VRID',
    'work_order_number',
    'last_move',
    'final_destination',
    'cargo_weight',
    't_state',
    'position',
    'freight_kind',
    'on_water',
    'reqs_power',
    'unit_impediments',
    'over_weight',
    'booking_number',
    'partnerShipmentId',
    'po_number',
    'Billing',
    'Shipment',
    'Mode',
    'CS NUMBER',
    'equipment_id',
    'chassis_number',
    'trailer_number',
    'category',
    'peel',
    'lcl',
    'leg_num',
    'bobtail',
];
// Define column categories
const COLUMN_CATEGORIES = {
    shipment_info: 'Basic Shipment Info',
    order_info: 'Order Info',
    carrier_info: 'Carrier Info',
    shipper_info: 'Shipper Info',
    dates: 'Dates and Times',
    financial: 'Financial Info',
    documents: 'Documents',
    tracking: 'Tracking and Status',
    reference_numbers: 'Reference Numbers',
    accessorials: 'Accessorials',
    billing: 'Billing',
    stops: 'Stops',
    equipment: 'Equipment',
    operations: 'Operations',
    other: 'Other'
};
// Define tooltips for specific columns
const COLUMN_TOOLTIPS = {
    shipment_id: 'Unique identifier for the shipment',
    order_id: 'Unique identifier for the order',
    shipment_status: 'Current status of the shipment',
    order_status: 'Current status of the order',
    billing_status: 'Current billing status',
    total: 'Total cost for the shipment',
    margin: 'Profit margin for the shipment',
    delivery_order_status: 'Status of delivery order document',
    exposed: 'Whether shipment is exposed to carrier network',
    has_required_docs: 'Whether all required documents are present',
    is_pod_valid: 'Whether proof of delivery is valid',
    missing_docs: 'Whether there are missing documents',
    pre_arrival_shipment: 'Whether this is a pre-arrival shipment'
};
// Define possible values for enumerated columns
const ENUMERATED_VALUES = {
    shipment_type: ['drayage', 'shorthaul'],
    order_type: ['import', 'export', 'domestic'],
    shipment_status: [
        'created', 'assigned', 'en_route_to_pickup', 'arrived_at_pickup',
        'loaded', 'en_route_to_delivery', 'arrived_at_delivery', 'delivered',
        'cancelled', 'on_hold'
    ],
    billing_status: [
        'not_ready', 'ready_to_bill', 'billed', 'invoiced', 'paid',
        'pending_approval', 'disputed'
    ],
    delivery_order_status: ['not-sent', 'sent', 'confirmed'],
    container_status: Object.values(CONTAINER_STATUS),
    service_type: ['standard', 'expedited', 'white_glove'],
    move_type: ['import', 'export', 'domestic'],
    mode: ['ocean', 'rail', 'truck'],
    equipment_type: ['20', '40', '40HC', '45', '53'],
    equipment_size: ['20', '40', '45', '53']
};
// Categorize columns based on their names and purposes
function categorizeColumn(columnName) {
    if (columnName.includes('shipment_id') || columnName.includes('shipment_oid') ||
        columnName.includes('shipment_') && !columnName.includes('cost') && !columnName.includes('total')) {
        return COLUMN_CATEGORIES.shipment_info;
    }
    if (columnName.includes('order_id') || columnName.includes('order_oid') ||
        columnName.includes('order_') && !columnName.includes('cost') && !columnName.includes('total')) {
        return COLUMN_CATEGORIES.order_info;
    }
    if (columnName.includes('carrier_') || columnName === 'driver_name' || columnName === 'driver_phone_number') {
        return COLUMN_CATEGORIES.carrier_info;
    }
    if (columnName.includes('shipper_') && !columnName.includes('cost') && !columnName.includes('total')) {
        return COLUMN_CATEGORIES.shipper_info;
    }
    if (columnName.includes('_date') || columnName.includes('_start') || columnName.includes('_end') ||
        columnName === 'created_at' || columnName === 'modified_at' || columnName.includes('_at')) {
        return COLUMN_CATEGORIES.dates;
    }
    if (columnName.includes('cost') || columnName.includes('total') || columnName.includes('rate') ||
        columnName.includes('margin') || columnName.includes('invoiced') || columnName.includes('payable') ||
        columnName.includes('receivable') || columnName === 'base_rate') {
        return COLUMN_CATEGORIES.financial;
    }
    if (columnName.includes('billing_') || columnName === 'billing_status') {
        return COLUMN_CATEGORIES.billing;
    }
    if (columnName.includes('docs') || columnName.includes('document') || columnName === 'delivery_order_status') {
        return COLUMN_CATEGORIES.documents;
    }
    if (columnName.includes('status') || columnName.includes('tracking') || columnName.includes('eta')) {
        return COLUMN_CATEGORIES.tracking;
    }
    if (STANDARD_REFERENCE_NUMBERS.includes(columnName) || columnName === 'reference_numbers') {
        return COLUMN_CATEGORIES.reference_numbers;
    }
    if (columnName.includes('accessorial') || columnName.includes('detention') || columnName.includes('storage') ||
        columnName.includes('chassis') || columnName.includes('demurrage')) {
        return COLUMN_CATEGORIES.accessorials;
    }
    if (columnName.includes('stop_') || columnName.includes('pickup') || columnName.includes('delivery')) {
        return COLUMN_CATEGORIES.stops;
    }
    if (columnName.includes('equipment_') || columnName.includes('chassis') || columnName.includes('container')) {
        return COLUMN_CATEGORIES.equipment;
    }
    if (columnName.includes('ops_') || columnName.includes('operations') || columnName.includes('_manager') ||
        columnName.includes('_rep') || columnName.includes('_agent')) {
        return COLUMN_CATEGORIES.operations;
    }
    return COLUMN_CATEGORIES.other;
}
// Generate user-facing label from column name
function generateLabel(columnName) {
    // Handle special cases
    const specialLabels = {
        shipment_id: 'Shipment ID',
        order_id: 'Order ID',
        shipment_oid: 'Shipment OID',
        order_oid: 'Order OID',
        equipment_id: 'Equipment ID',
        carrier_oid: 'Carrier OID',
        shipper_oid: 'Shipper OID',
        bco: 'BCO',
        lfd: 'LFD',
        eta: 'ETA',
        so_number: 'SO Number',
        pu_number: 'PU Number',
        mg_load_id: 'MG Load ID',
        mo_number: 'MO Number',
        pod_status: 'POD Status'
    };
    if (specialLabels[columnName]) {
        return specialLabels[columnName];
    }
    // Convert snake_case to Title Case
    return titleize(humanize(columnName));
}
// Check if column is visible to shippers
function isVisibleToShippers(columnName) {
    return !SHIPPER_BLACKLIST.includes(columnName);
}
// Check if column is visible to carriers
function isVisibleToCarriers(columnName) {
    return !CARRIER_BLACKLIST.includes(columnName);
}
// Check if column is included in default view
function isInDefaultView(columnName) {
    // Define columns that are typically shown in default view
    const defaultViewColumns = [
        'shipment_id', 'order_id', 'shipment_status', 'shipper_name', 'carrier_name',
        'pickup_start', 'deliver_start', 'total', 'margin', 'created_at'
    ];
    return defaultViewColumns.includes(columnName);
}
// Check if column is inline editable
function isInlineEditable(columnName) {
    // Define columns that can be edited inline
    const editableColumns = [
        'admin_notes', 'notes', 'tags', 'delivery_order_status', 'shipment_status'
    ];
    return editableColumns.includes(columnName);
}
// Check if column allows bulk edit
function allowsBulkEdit(columnName) {
    // Define columns that support bulk editing
    const bulkEditColumns = [
        'tags', 'delivery_order_status', 'admin_notes', 'shipper_operations_group',
        'shipper_operations_manager', 'shipper_billing_rep',
        'shipper_sales_rep',
        'carrier_cost_total'
    ];
    return bulkEditColumns.includes(columnName);
}
let shipmentFields;
let orderFields;
// Determine if a column belongs to Shipment, Order, or Both
function getShipmentOrOrder(columnName) {
    // Helper to check if a column exists in a set, considering shipment_ prefix stripping
    const existsInSet = (col, fieldSet) => {
        if (fieldSet.has(col))
            return true;
        return false;
    };
    const inShipment = existsInSet(columnName, shipmentFields);
    const inOrder = existsInSet(columnName, orderFields);
    if (inShipment && inOrder)
        return 'Both';
    if (inShipment)
        return 'Shipment';
    if (inOrder)
        return 'Order';
    return '';
}
const flattenColumns = (columns, result = []) => {
    for (const col of columns) {
        if (col.children && Array.isArray(col.children)) {
            flattenColumns(col.children, result);
        }
        else {
            result.push(col);
        }
    }
    return result;
};
const csvHeader = 'Column Slug,Column Label (English),Column Category,Tooltip Text,Possible Values,Visible to Shippers,Visible to Carriers,Included in Default View,Inline Editable,Bulk Editable,Order/Shipment\n';
const generateUVColumnConfigDownload = (t) => {
    const flatShipmentColumns = flattenColumns(shipmentColumns);
    const referenceNumberGroup = flattenColumns(getReferenceNumberGroup());
    const drColumnDefs = flattenColumns(getColumnDefs('drayage', t));
    const shColumnDefs = flattenColumns(getColumnDefs('', t));
    const flatOrderColumns = flattenColumns(orderColumns);
    // Get all shipment schema fields (Universal + Drayage)
    shipmentFields = new Set([...flatShipmentColumns.map(col => col.field),
        ...referenceNumberGroup.map(col => col.field),
        ...drColumnDefs.map(col => col.field),
        ...shColumnDefs.map(col => col.field)]);
    // Get all order schema fields
    orderFields = new Set(flatOrderColumns.map(col => col.field));
    const rows = [csvHeader];
    const processedColumns = new Set();
    // Build CSV rows directly without intermediate metadata objects
    const processColumn = ({ field: columnName }) => {
        if (!columnName || columnName.trim() === '') {
            throw new TypeError(`Expected non-empty string for column field name, received: ${columnName}`);
        }
        if (processedColumns.has(columnName))
            return;
        processedColumns.add(columnName);
        rows.push([
            columnName,
            `"${generateLabel(columnName)}"`,
            `"${categorizeColumn(columnName)}"`,
            `"${COLUMN_TOOLTIPS[columnName] || ''}"`,
            `"${ENUMERATED_VALUES[columnName] ? ENUMERATED_VALUES[columnName].join(', ') : ''}"`,
            isVisibleToShippers(columnName) ? 'Yes' : 'No',
            isVisibleToCarriers(columnName) ? 'Yes' : 'No',
            isInDefaultView(columnName) ? 'Yes' : 'No',
            isInlineEditable(columnName) ? 'Yes' : 'No',
            allowsBulkEdit(columnName) ? 'Yes' : 'No',
            getShipmentOrOrder(columnName)
        ].join(',') + '\n');
    };
    flatShipmentColumns.forEach(processColumn);
    referenceNumberGroup.forEach(processColumn);
    drColumnDefs.forEach(processColumn);
    shColumnDefs.forEach(processColumn);
    flatOrderColumns.forEach(processColumn);
    return rows.join('');
};
export default generateUVColumnConfigDownload;
